import { useEffect, useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { Button, Heading } from "suomifi-ui-components";
import { IconCheckCircle, IconClose } from "suomifi-icons";
import { annaProfiiliTrivoreKayttajalle, voiNahda } from "../utils/oikeudet";
import { useGetOfficersQuery } from "../api/backendApi";
import { useTodellinenRooli } from "../hooks/useTodellinenRooli";
import { useTranslation } from "react-i18next";

/* Vaalea vihreä success-tausta (success-hue 166, L94 % — sama valööri kuin
   DS:n muut vaaleat tintit). Jaettu success-bannerin ja lisätyn rivin
   korostuksen kesken, jotta ne ovat identtiset. DS:n vihreä paletti ei tarjoa
   näin vaaleaa tokenia → status-väri hienosäädetty DS:n värifamiliassa
   (sallittu CLAUDE.md "Teeman muokkaus"). */
const SUCCESS_BG = "hsl(166, 60%, 94%)";

/**
 * Käyttöoikeuksien myöntäminen — virkailijalista (K5, 3c).
 * Lähde: 10.6.2026_UX-suunnittelu_kayttooikeuksien_myontaminen.md §3.2.
 *
 * Koko rivi klikattava → KayttooikeusKortti. Haku + tila-filtteri
 * (Aktiiviset / Poistetut). "+ Lisää virkailija" → sähköpostipohjainen
 * lomake → ohjaa myöntökortille.
 *
 * Turvallisuus: oma rivi ei-klikattava (ei omien oikeuksien muokkausta).
 */

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.m};
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.m};
  flex-wrap: wrap;
`;

const FiltteriBar = styled.div`
  background: ${({ theme }) => theme.colors.whiteBase};
  border: 1px solid ${({ theme }) => theme.colors.depthLight1};
  padding: ${({ theme }) => theme.spacing.m};
  display: flex;
  gap: ${({ theme }) => theme.spacing.m};
  flex-wrap: wrap;
  align-items: center;
`;

const TableWrap = styled.div`
  background: ${({ theme }) => theme.colors.whiteBase};
  border: 1px solid ${({ theme }) => theme.colors.depthLight1};
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  min-width: 42rem;
  border-collapse: collapse;
  font: ${({ theme }) => theme.typography.bodyTextSmall};
`;

const Thead = styled.thead`
  background: ${({ theme }) => theme.colors.depthLight3};
  border-bottom: 2px solid ${({ theme }) => theme.colors.brandBase};
`;

const Th = styled.th<{ $w?: string }>`
  text-align: left;
  padding: ${({ theme }) => theme.spacing.s} ${({ theme }) => theme.spacing.s};
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.brandBase};
  ${({ $w }) => $w && `width: ${$w};`}
`;

const Tbody = styled.tbody`
  & tr + tr {
    border-top: 1px solid ${({ theme }) => theme.colors.depthLight2};
  }
`;

const Tr = styled.tr<{
  $klikattava: boolean;
  $poistettu?: boolean;
  $korostettu?: boolean;
}>`
  cursor: ${({ $klikattava }) => ($klikattava ? "pointer" : "default")};
  transition: background 0.15s ease;
  /* Korostettu = juuri lisätty virkailija (vaalea vihreä, sama kuin success-
     banneri). Poistettu rivi: kevyt harmaa. */
  background: ${({ theme, $poistettu, $korostettu }) =>
    $korostettu
      ? SUCCESS_BG
      : $poistettu
      ? theme.colors.depthLight3
      : "transparent"};

  &:hover {
    background: ${({ theme, $klikattava, $poistettu, $korostettu }) =>
      $korostettu
        ? SUCCESS_BG
        : $klikattava
        ? theme.colors.highlightLight4
        : $poistettu
        ? theme.colors.depthLight3
        : "transparent"};
  }
`;

const Td = styled.td`
  padding: ${({ theme }) => theme.spacing.s} ${({ theme }) => theme.spacing.s};
  vertical-align: middle;
  font-size: 0.875rem;
`;

const VirkailijaNimi = styled.div<{ $poistettu?: boolean }>`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.875rem;
  color: ${({ theme, $poistettu }) =>
    $poistettu ? theme.colors.depthDark1 : theme.colors.blackBase};
`;

const VirkailijaSahkoposti = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.depthDark1};
  margin-top: ${({ theme }) => theme.spacing.insetXxs};
`;

const RooliTagi = styled.span<{ $poistettu?: boolean }>`
  display: inline-flex;
  /* Poistetun rivin tag vaimeampi (harmaa) — sama muoto, eri sävy. */
  background: ${({ theme, $poistettu }) =>
    $poistettu ? theme.colors.depthLight2 : theme.colors.highlightLight4};
  color: ${({ theme, $poistettu }) =>
    $poistettu ? theme.colors.depthDark1 : theme.colors.brandBase};
  border: 1px solid
    ${({ theme, $poistettu }) =>
      $poistettu ? theme.colors.depthLight1 : theme.colors.highlightLight2};
  padding: ${({ theme }) => theme.spacing.insetXxs}
    ${({ theme }) => theme.spacing.xs};
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.6875rem;
`;

const OmaTag = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.depthDark1};
  font-style: italic;
  margin-left: ${({ theme }) => theme.spacing.xs};
`;

const PoistettuMerkki = styled.div`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.depthDark1};
  margin-top: ${({ theme }) => theme.spacing.insetXxs};
`;

const TilaPillerit = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.s};
  flex-wrap: wrap;
`;

const TilaPilleri = styled.button<{ $aktiivinen: boolean }>`
  background: ${({ theme, $aktiivinen }) =>
    $aktiivinen ? theme.colors.brandBase : "transparent"};
  color: ${({ theme, $aktiivinen }) =>
    $aktiivinen ? theme.colors.whiteBase : theme.colors.depthDark1};
  border: 1px solid
    ${({ theme, $aktiivinen }) =>
      $aktiivinen ? theme.colors.brandBase : theme.colors.depthLight1};
  padding: ${({ theme }) => theme.spacing.xxs} ${({ theme }) => theme.spacing.s};
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.8125rem;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.highlightBase};
  }
`;

/* Success-ilmoitus virkailijan lisäyksen jälkeen (E4). Kiinteä (ei toast),
   vihreä, suljettava oikean laidan x-painikkeella. Ei katoa automaattisesti;
   katoaa kun käyttäjä sulkee sen TAI sivu ladataan uudelleen (history-state
   tyhjennetään mountissa). role="status" + aria-live → ruudunlukija ilmoittaa. */
const SuccessBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.s};
  background: ${SUCCESS_BG};
  /* DS InlineAlert -malli (tismalleen sama tyyli): kevyt koko laatikon kehys =
     statusBase 20 %:n opasiteetilla (DS: transparentize(0.8, base)) → tässä
     successBase (hsl(166,90%,34%)) @ 20 %. Vasen raita = vahva väri
     (successDark1, kontrasti ≥ 3:1). */
  border: 1px solid hsla(166, 90%, 34%, 0.2);
  border-left: 4px solid ${({ theme }) => theme.colors.successDark1};
  padding: ${({ theme }) => theme.spacing.s} ${({ theme }) => theme.spacing.m};
`;

const SuccessIkoni = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  margin-top: 0.1rem;
  & > svg {
    width: 1.25rem;
    height: 1.25rem;
  }
  & > svg .fi-icon-base-fill {
    fill: ${({ theme }) => theme.colors.successDark1};
  }
`;

const SuccessText = styled.div`
  flex: 1;
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.blackBase};
`;

const SuljeNappi = styled.button`
  flex-shrink: 0;
  background: transparent;
  border: 0;
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xxs};
  display: inline-flex;
  align-items: center;

  & > svg {
    width: 1rem;
    height: 1rem;
  }
  /* Sulku-X tummana (neutraali, selkeä kontrasti vaaleanvihreällä). Hoverissa
     vain ikonin värimuutos (→ successDark1) — ei taustalaatikkoa, joka näytti
     irralliselta vihreän bannerin päällä. Kursori + focus-outline kantavat
     klikattavuuden. */
  & > svg .fi-icon-base-fill {
    fill: ${({ theme }) => theme.colors.blackBase};
    transition: fill 0.12s ease;
  }
  &:hover > svg .fi-icon-base-fill {
    fill: ${({ theme }) => theme.colors.successDark1};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.highlightBase};
    outline-offset: 2px;
  }
`;

type TilaFiltteri = "aktiivinen" | "poistettu" | "kaikki";

export default function Kayttooikeudet() {
  const { t, i18n } = useTranslation(["oikeudet", "common"]);
  const navigate = useNavigate();
  const location = useLocation();

  const [tilaFiltteri, setTilaFiltteri] = useState<TilaFiltteri>("aktiivinen");
  const piilotaRaportit = import.meta.env.VITE_PIILOTA_RAPORTIT === "true";
  const [lisatty, setLisatty] = useState<{ nimi: string; uid: string } | null>(
    () => {
      const s = location.state as {
        lisattyNimi?: string;
        lisattyUid?: string;
      } | null;
      return s?.lisattyNimi && s?.lisattyUid
        ? { nimi: s.lisattyNimi, uid: s.lisattyUid }
        : null;
    },
  );

  const { kayttaja, todellinenRooli, isLoading, error } = useTodellinenRooli();
  const {
    data: officers,
    isLoading: isLoadingOfficers,
    error: errorOfficers,
  } = useGetOfficersQuery();

  useEffect(() => {
    if ((location.state as { lisattyNimi?: string } | null)?.lisattyNimi) {
      window.history.replaceState({}, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- All conditional returns AFTER every hook ---
  if (isLoading || isLoadingOfficers) return <>{t("common:ladataan")}</>;
  if (error || !kayttaja) return <>{t("virheet.kayttaja")}</>;
  if (errorOfficers || !officers) return <>{t("virheet.virkailija")}</>;
  if (!voiNahda("navi.kayttooikeudet", todellinenRooli?.profiili)) {
    return <Navigate to="/" replace />;
  }

  // --- Map Trivore officers → rows the table renders ---
  const naytettavat = officers
    .map((o) => {
      const profiili = annaProfiiliTrivoreKayttajalle({ memberOf: o.memberOf });
      const tila: "aktiivinen" | "poistettu" = o.softDeleted
        ? "poistettu"
        : "aktiivinen";
      return {
        uid: o.id,
        nimi: `${o.name.givenName ?? ""} ${o.name.familyName ?? ""}`.trim(),
        sahkoposti: o.email ?? "",
        rooli: profiili?.id
          ? t(`common:roolit.${profiili.id}`)
          : t("common:roolit.ei_roolia"),
        tila,
      };
    })
    .sort((a, b) => a.nimi.localeCompare(b.nimi, "fi"));

  const suodatetut =
    tilaFiltteri === "kaikki"
      ? naytettavat
      : naytettavat.filter((v) => v.tila === tilaFiltteri);

  return (
    <Page>
      <TopRow>
        <Heading variant="h1">{t("otsikot.oikeudet")}</Heading>
        <Button onClick={() => navigate("/kayttooikeudet/lisaa")}>
          {t("napit.lisaa_virkailija")}
        </Button>
      </TopRow>

      {lisatty && (
        <SuccessBanner role="status" aria-live="polite">
          <SuccessIkoni>
            <IconCheckCircle aria-hidden />
          </SuccessIkoni>
          <SuccessText>
            {t("viestit.onnistui1")} <strong>{lisatty.nimi}</strong>{" "}
            {t("viestit.onnistui2")}
          </SuccessText>
          <SuljeNappi
            type="button"
            onClick={() => setLisatty(null)}
            aria-label={t("napit.sulje")}
          >
            <IconClose aria-hidden />
          </SuljeNappi>
        </SuccessBanner>
      )}

      <FiltteriBar>
        <TilaPillerit>
          <TilaPilleri
            type="button"
            $aktiivinen={tilaFiltteri === "aktiivinen"}
            onClick={() => setTilaFiltteri("aktiivinen")}
            aria-pressed={tilaFiltteri === "aktiivinen"}
          >
            {t("tilat.aktiiviset")}
          </TilaPilleri>
          <TilaPilleri
            type="button"
            $aktiivinen={tilaFiltteri === "poistettu"}
            onClick={() => setTilaFiltteri("poistettu")}
            aria-pressed={tilaFiltteri === "poistettu"}
          >
            {t("tilat.poistetut")}
          </TilaPilleri>
          <TilaPilleri
            type="button"
            $aktiivinen={tilaFiltteri === "kaikki"}
            onClick={() => setTilaFiltteri("kaikki")}
            aria-pressed={tilaFiltteri === "kaikki"}
          >
            {t("tilat.kaikki_capFirst")}
          </TilaPilleri>
        </TilaPillerit>
      </FiltteriBar>

      <TableWrap>
        <Table>
          <Thead>
            <tr>
              <Th>{t("otsikot.virkailija")}</Th>
              <Th $w="18rem">{t("otsikot.roolit")}</Th>
            </tr>
          </Thead>
          <Tbody>
            {suodatetut.map((v) => {
              const omaRivi = v.uid === kayttaja?.id;
              const klikattava = !omaRivi;
              const poistettu = v.tila === "poistettu";
              return (
                <Tr
                  key={v.uid}
                  $klikattava={klikattava}
                  $poistettu={poistettu}
                  $korostettu={lisatty?.uid === v.uid}
                  onClick={() => {
                    if (klikattava) navigate(`/kayttooikeudet/${v.uid}`);
                  }}
                  tabIndex={klikattava ? 0 : undefined}
                  role={klikattava ? "button" : undefined}
                  aria-label={
                    klikattava
                      ? `${t("labels.virkailija1")} ${v.nimi} ${t(
                          "labels.virkailija2",
                        )}`
                      : undefined
                  }
                  onKeyDown={(e) => {
                    if (klikattava && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      navigate(`/kayttooikeudet/${v.uid}`);
                    }
                  }}
                >
                  <Td>
                    <VirkailijaNimi $poistettu={poistettu}>
                      {v.nimi}
                    </VirkailijaNimi>
                    <VirkailijaSahkoposti>{v.sahkoposti}</VirkailijaSahkoposti>
                    {poistettu && (
                      <PoistettuMerkki>{t("merkit.poistettu")}</PoistettuMerkki>
                    )}
                  </Td>
                  <Td>
                    <RooliTagi $poistettu={poistettu}>{v.rooli}</RooliTagi>
                    {omaRivi && <OmaTag>{t("tags.oma_tili")}</OmaTag>}
                  </Td>
                </Tr>
              );
            })}
            {suodatetut.length === 0 && (
              <tr>
                <Td colSpan={2} style={{ textAlign: "center", color: "#666" }}>
                  {t("ei.virkailijoita")}
                </Td>
              </tr>
            )}
          </Tbody>
        </Table>
      </TableWrap>
    </Page>
  );
}
